import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, TextField,
  InputAdornment, CircularProgress, Alert
} from '@mui/material';
import { Add, Edit, Delete, Search, Star, Visibility, Link as LinkIcon } from '@mui/icons-material';
import api from '../services/api';

export default function Replays() {
  const [replays, setReplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchReplays();
  }, []);

  const fetchReplays = async () => {
    try {
      setLoading(true);
      const res = await api.get('/replays/all');
      setReplays(res.data);
    } catch (err) {
      setError('Erreur lors du chargement des replays');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce replay ?')) return;
    try {
      await api.delete(`/replays/${id}`);
      setReplays(replays.filter((r) => r._id !== id));
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const filteredReplays = replays.filter((r) =>
    r.title?.toLowerCase().includes(search.toLowerCase()) ||
    r.presenters?.some((p) => p.name?.toLowerCase().includes(search.toLowerCase()))
  );

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Replays SkinKnowledge</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/replays/new')}
        >
          Nouveau Replay
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher un replay..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Presentateur</TableCell>
              <TableCell>Session liee</TableCell>
              <TableCell>Vues</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReplays.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Aucun replay trouve
                </TableCell>
              </TableRow>
            ) : (
              filteredReplays.map((replay) => (
                <TableRow key={replay._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(replay.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{replay.title}</Typography>
                    {replay.isFeatured && (
                      <Chip
                        icon={<Star />}
                        label="A la une"
                        size="small"
                        color="warning"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {replay.presenters?.[0]?.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {replay.sessionId?.title ? (
                      <Chip
                        icon={<LinkIcon />}
                        label={replay.sessionId.title}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: 200 }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">Aucune</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Visibility fontSize="small" color="action" />
                      <Typography variant="body2">{replay.viewCount || 0}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {replay.isPublished ? (
                      <Chip label="Publie" size="small" color="success" />
                    ) : (
                      <Chip label="Brouillon" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/replays/${replay._id}`)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(replay._id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          {filteredReplays.length} replay(s) - {filteredReplays.filter((r) => r.isPublished).length} publie(s)
        </Typography>
      </Box>
    </Box>
  );
}
